import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import useSWR from "swr";
import { API, fetcher } from "../lib/api";
import { useCart } from "../context/CartContext";
import { categoryName } from "../data/site";
import ProductCard from "./ProductCard";

const easeOut = [0.16, 1, 0.3, 1];

export default function FeaturedProducts() {
  const { data: products = [] } = useSWR(`${API}/products`, fetcher);
  const { addItem } = useCart();
  const featured = products.filter((p) => p.featured).slice(0, 3);
  const [hero, ...rest] = featured;

  const addHero = () => {
    if (!hero) return;
    const size = hero.sizes?.[1] || hero.sizes?.[0] || "One Size";
    addItem({
      productId: hero.id,
      name: hero.name,
      price: hero.price,
      image: hero.image,
      size,
    });
    toast.success(`${hero.name} added to your bag`, {
      description: `Size ${size} · £${hero.price.toFixed(2)}`,
    });
  };

  return (
    <section className="py-20 md:py-24" data-testid="featured-products-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: easeOut }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-blush-700">
              Fresh & Loved
            </p>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
              Picked to make them sparkle
            </h2>
          </motion.div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
          {hero && (
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, ease: easeOut }}
              className="md:col-span-8 md:row-span-2"
              data-testid={`featured-card-${hero.id}`}
            >
              <div className="group relative h-full min-h-[420px] overflow-hidden rounded-[36px] md:min-h-[520px]">
                <img
                  src={hero.image}
                  alt={hero.name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                {hero.badge && (
                  <span className="absolute left-5 top-5 rounded-full bg-white/90 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-ink backdrop-blur">
                    {hero.badge}
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 p-7 md:p-9">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-blush-300">
                    {categoryName(hero.category_id)}
                  </p>
                  <h3 className="mt-2 max-w-md font-display text-2xl font-semibold text-white md:text-3xl">
                    {hero.name}
                  </h3>
                  <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                    <span className="font-display text-2xl text-white">
                      £{hero.price.toFixed(2)}
                      {hero.compare_at != null && hero.compare_at > hero.price && (
                        <span className="ml-2 text-base font-normal text-white/60 line-through">
                          £{hero.compare_at.toFixed(2)}
                        </span>
                      )}
                    </span>
                    <button
                      data-testid={`add-to-bag-${hero.id}`}
                      onClick={addHero}
                      className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-display text-sm font-semibold text-ink shadow-lg transition-transform duration-300 hover:scale-105"
                    >
                      <ShoppingBag className="h-4 w-4" />
                      Add to Bag
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {rest.map((p, i) => (
            <ProductCard
              key={p.id}
              product={p}
              index={i}
              className="md:col-span-4"
            />
          ))}
        </div>
      </div>
    </section>
  );
}
